// Shared firm-tag catalog loader.
//
// TagChipsSection (the per-row picker) already lazy-loads the catalog for its
// own dropdown.  The bulk-action bars on Leads + Contacts need the same list,
// and duplicating the fetch + loading + empty handling in two pages is how the
// two surfaces drift apart — in particular on the empty state, which is the
// state every user hits first (the firm has zero tags today, so an
// unguarded picker would render an empty dropdown and look broken).
//
// Lazily fetches when `enabled` flips true, once, and caches for the mount.
//
//   const { tags, loading, loaded, reload } = useTagCatalog(pickerOpen)

import { useCallback, useEffect, useRef, useState } from 'react'
import { listTags } from '../services/api'

export default function useTagCatalog (enabled) {
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // Guards against a second fetch when the consumer re-opens the picker.
  const fetchedRef = useRef(false)
  // Prevents a setState after unmount when the request is still in flight.
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await listTags()
      const rows = r?.data?.tags || r?.data || []
      if (aliveRef.current) setTags(Array.isArray(rows) ? rows : [])
    } catch {
      // A failed catalog fetch is indistinguishable from "no tags" to the
      // user, so report loaded-with-nothing and let the caller's empty state
      // do the talking rather than rendering a silent dead dropdown.
      if (aliveRef.current) setTags([])
    } finally {
      if (aliveRef.current) { setLoading(false); setLoaded(true) }
    }
  }, [])

  useEffect(() => {
    if (!enabled || fetchedRef.current) return
    fetchedRef.current = true
    load()
  }, [enabled, load])

  const reload = useCallback(() => { fetchedRef.current = true; return load() }, [load])

  return { tags, loading, loaded, reload }
}
